import winkNLP from 'wink-nlp';
import model from 'wink-eng-lite-web-model';

export class KeywordGenerator {
  private static nlp = winkNLP(model);
  private static its = KeywordGenerator.nlp.its;
  private static as = KeywordGenerator.nlp.as;

  private static STOP_WORDS = new Set<string>([
    'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'for', 'to', 'with',
    'by', 'of', 'from', 'about', 'as', 'into', 'like', 'through', 'after', 'over',
    'between', 'out', 'against', 'during', 'without', 'before', 'under', 'around',
    'among', 'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you',
    'your', 'yours', 'yourself', 'yourselves', 'he', 'him', 'his', 'himself',
    'she', 'her', 'hers', 'herself', 'it', 'its', 'itself', 'they', 'them',
    'their', 'theirs', 'themselves', 'what', 'which', 'who', 'whom', 'this',
    'that', 'these', 'those', 'am', 'is', 'are', 'was', 'were', 'be', 'been',
    'being', 'have', 'has', 'had', 'having', 'do', 'does', 'did', 'doing',
    'will', 'would', 'should', 'can', 'could', 'ought', 'might', 'must',
    'pc', 'computer', 'premium', 'solutions', 'hardware', 'browse', 'discover'
  ]);

  private static BRAND_KEYWORDS = [
    'BattleforgePC',
    'custom gaming pc',
    'prebuilt gaming pc'
  ];

  /**
   * Generates a frequency-weighted list of keywords from a block of text.
   */
  private static extractKeywords(text: string, maxLength = 10): string[] {
    if (!text) return [];

        const doc = KeywordGenerator.nlp.readDoc(text.toLowerCase());

    const lemmas = doc
      .tokens()
      .filter(token =>
        token.out(KeywordGenerator.its.type) === 'word' &&
        !KeywordGenerator.STOP_WORDS.has(token.out(KeywordGenerator.its.normal)) &&
        ['NOUN', 'ADJ', 'VERB'].includes(token.out(KeywordGenerator.its.pos))
      )
      .out(KeywordGenerator.its.lemma);

    const frequencyMap: Record<string, number> = {};

    for (const word of lemmas) {
      frequencyMap[word] = (frequencyMap[word] || 0) + 1;
    }

    return Object.entries(frequencyMap)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([word]) => word)
      .slice(0, maxLength);
  }

  /**
   * Generates a final, comma-separated meta keywords string.
   */
  public static generateMetaKeywords(
    title: string,
    description: string,
    customKeywords?: string
  ): string {
    const titleKeywords = this.extractKeywords(title);
    const descriptionKeywords = this.extractKeywords(description);

    const customKeywordsArray = customKeywords
      ? customKeywords
          .split(/[,|;]/)
          .map(k => k.trim().toLowerCase())
          .filter(Boolean)
      : [];

    const allKeywords = new Set<string>([
      ...titleKeywords,
      ...descriptionKeywords,
      ...customKeywordsArray,
      ...this.BRAND_KEYWORDS.map(k => k.toLowerCase()),
    ]);

    return Array.from(allKeywords).join(', ');
  }
}

/**
 * SEO utility functions
 */

/**
 * Generates an SEO-friendly slug from a PC name
 * Format: {pc-name}-prebuiltpc-battleforgepc
 * Example: "iron-lite-prebuiltpc-battleforgepc"
 */
export function generatePCSlug(pcName: string): string {
  return `${pcName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim()
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }-prebuiltpc-battleforgepc`;
}

/**
 * Extracts the PC name from an SEO-friendly slug
 * Example: "iron-lite-prebuiltpc-battleforgepc" -> "Iron Lite"
 */
export function extractPCNameFromSlug(slug: string): string {
  // Remove the suffix "-prebuiltpc-battleforgepc"
  const namePart = slug.replace(/-prebuiltpc-battleforgepc$/, '');
  
  // Convert back to readable format
  return namePart
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Creates a mapping of PC slugs to their IDs for quick lookup
 */
export function createPCSlugMap(pcSystems: Array<{ id: string; name: string }>) {
  const slugMap = new Map<string, string>();
  
  pcSystems.forEach(pc => {
    const slug = generatePCSlug(pc.name);
    slugMap.set(slug, pc.id);
  });
  
  return slugMap;
}

/**
 * Creates a reverse mapping of PC IDs to their slugs
 */
export function createPCIdToSlugMap(pcSystems: Array<{ id: string; name: string }>) {
  const idToSlugMap = new Map<string, string>();
  
  pcSystems.forEach(pc => {
    const slug = generatePCSlug(pc.name);
    idToSlugMap.set(pc.id, slug);
  });
  
  return idToSlugMap;
}
